import { JWT } from 'next-auth/jwt'
import { signOut } from 'next-auth/react'
import { EMAIL_VERIFICATION_MAX_AGE } from '../constants/settings'

export const getBearerToken = async (): Promise<string | undefined> => {
  const data = await fetch(`${process.env.AUTH0_ISSUER_BASE_URL}/oauth/token`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      client_id: process.env.AUTH0_CLIENT_ID,
      client_secret: process.env.AUTH0_CLIENT_SECRET,
      audience: `${process.env.AUTH0_ISSUER_BASE_URL}/api/v2/`,
      grant_type: `client_credentials`,
    }),
  })
  const result = await data.json()

  if (!result.access_token) {
    console.error('Failed to get bearer token:', result)
  }

  return result.access_token
}

export type Auth0TokenResponse = {
  access_token: string
  refresh_token: string
  id_token: string
  scope: string
  expires_in: number
  token_type: string
}

export const getAccessToken = async (
  refreshToken: string,
): Promise<Auth0TokenResponse | undefined> => {
  try {
    const data = await fetch(
      `${process.env.AUTH0_ISSUER_BASE_URL}/oauth/token`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: process.env.AUTH0_CLIENT_ID,
          client_secret: process.env.AUTH0_CLIENT_SECRET,
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
        }),
      },
    )

    const result = await data.json()

    return result
  } catch (error) {
    console.error('Could get accesstoken', error)

    return undefined
  }
}

export const passwordChangeTicket = async (
  email: string,
  type: 'invitation' | 'reset',
  language?: string,
  userId?: string,
): Promise<string> => {
  const bearerToken = await getBearerToken()

  if (!bearerToken) {
    throw new Error('Failed to get Auth0 bearer token')
  }

  // Use provided userId or look up by email
  let auth0UserId = userId

  if (!auth0UserId) {
    const users = await getAuth0UsersByEmail(bearerToken, email)

    if (!users || users.length === 0) {
      throw new Error(`User not found: ${email}`)
    }

    auth0UserId = users[0].user_id
  }

  const resultUrl =
    language === 'da'
      ? process.env.VERCEL_URL_MBO || process.env.NEXT_PUBLIC_VERCEL_URL_MBO
      : process.env.VERCEL_URL || process.env.NEXT_PUBLIC_VERCEL_URL

  const response = await fetch(
    `${process.env.AUTH0_ISSUER_BASE_URL}/api/v2/tickets/password-change`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        authorization: `Bearer ${bearerToken}`,
      },
      body: JSON.stringify({
        user_id: auth0UserId,
        ttl_sec: EMAIL_VERIFICATION_MAX_AGE,
        mark_email_as_verified: false,
        result_url: resultUrl,
      }),
    },
  )

  // Retry on 429 with exponential backoff, honouring Retry-After if present
  if (response.status === 429) {
    const MAX_RETRIES = 4
    let attempt = 0
    let lastResponse = response

    while (attempt < MAX_RETRIES) {
      attempt++
      const retryAfterHeader = lastResponse.headers.get('Retry-After')
      const retryAfterMs = retryAfterHeader
        ? parseInt(retryAfterHeader, 10) * 1000
        : Math.min(1000 * 2 ** attempt, 30000) // 2s, 4s, 8s, 16s

      console.warn(
        `Auth0 rate limit on password-change ticket (attempt ${attempt}/${MAX_RETRIES}), retrying in ${retryAfterMs}ms`,
      )
      await new Promise((r) => setTimeout(r, retryAfterMs))

      lastResponse = await fetch(
        `${process.env.AUTH0_ISSUER_BASE_URL}/api/v2/tickets/password-change`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            authorization: `Bearer ${bearerToken}`,
          },
          body: JSON.stringify({
            user_id: auth0UserId,
            ttl_sec: EMAIL_VERIFICATION_MAX_AGE,
            mark_email_as_verified: false,
            result_url: resultUrl,
          }),
        },
      )

      if (lastResponse.status !== 429) {
        break
      }
    }

    if (lastResponse.status === 429) {
      const error = await lastResponse.text()
      throw new Error(
        `Failed to create password change ticket after ${MAX_RETRIES} retries (rate limited): ${error}`,
      )
    }

    // Successful retry — check for non-429 errors before parsing
    if (!lastResponse.ok) {
      const error = await lastResponse.text()
      throw new Error(`Failed to create password change ticket: ${error}`)
    }

    const retryTicket = await lastResponse.json()
    const retryTicketUrl = new URL(retryTicket.ticket)
    retryTicketUrl.searchParams.set('type', type)
    if (language) {
      retryTicketUrl.searchParams.set('lang', language)
    }
    return retryTicketUrl.toString()
  }

  if (!response.ok) {
    const error = await response.text()
    throw new Error(`Failed to create password change ticket: ${error}`)
  }

  const ticket = await response.json()

  // Append language and type to the ticket URL
  const ticketUrl = new URL(ticket.ticket)
  ticketUrl.searchParams.set('type', type)
  if (language) {
    ticketUrl.searchParams.set('lang', language)
  }

  return ticketUrl.toString()
}

export const verifyAuth0Password = async (
  username: string,
  password: string,
) => {
  const res = await fetch(`${process.env.AUTH0_ISSUER_BASE_URL}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client_id: process.env.AUTH0_CLIENT_ID,
      client_secret: process.env.AUTH0_CLIENT_SECRET,
      grant_type: 'password',
      username,
      password,
      scope: 'openid',
      connection: 'Username-Password-Authentication',
    }),
  })

  return res.status === 200
}

export const getAuth0UsersByEmail = async (
  bearerToken: string,
  email: string,
): Promise<
  {
    identities: {
      provider: string
      user_id: string
      connection: string
      isSocial: boolean
    }[]
    user_id: string
  }[]
> => {
  const str = `/api/v2/users-by-email?fields=user_id,identities&include_fields=true&email=${encodeURIComponent(
    email,
  )}`

  const data = await fetch(`${process.env.AUTH0_ISSUER_BASE_URL}${str}`, {
    method: 'GET',
    headers: { authorization: `Bearer ${bearerToken}` },
  })

  if (data.status < 200 || data.status > 299) {
    throw Error(data.statusText)
  }
  return (await data.json()) || []
}

export const updateAuth0User = async (
  bearerToken: string,
  userId: string,
  payload: object,
): Promise<Response> => {
  return fetch(`${process.env.AUTH0_ISSUER_BASE_URL}/api/v2/users/${userId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${bearerToken}`,
    },
    body: JSON.stringify(payload),
  })
}

export const setAuth0PasswordByUserId = async (
  bearerToken: string,
  userId: string,
  password: string,
): Promise<Response> => {
  return updateAuth0User(bearerToken, userId, { password })
}
export const handleSignOut = async () => {
  await signOut({ redirect: false })
  const currentOrigin =
    process.env.NODE_ENV === 'development'
      ? process.env.NEXT_PUBLIC_VERCEL_URL || ''
      : window.location.origin
  window.location.href = `${
    process.env.NEXT_PUBLIC_AUTH0_ISSUER_BASE_URL
  }/v2/logout?client_id=${
    process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID
  }&returnTo=${encodeURIComponent(currentOrigin)}`
}

export type Auth0Token = {
  accessToken?: string
  accessTokenExpires: number
  idToken?: string
  refreshToken?: string
}

export const refreshAuth0Token = async ({
  accessToken,
  accessTokenExpires,
  idToken,
  refreshToken,
}: JWT): Promise<Auth0Token> => {
  try {
    const refreshedTokens = await getAccessToken(refreshToken || '')

    if (refreshedTokens) {
      const accessTokenExpires = Date.now() + refreshedTokens.expires_in * 1000

      const result = {
        accessToken: refreshedTokens.access_token,
        accessTokenExpires,
        idToken: refreshedTokens.id_token,
        refreshToken: refreshedTokens.refresh_token ?? refreshToken,
      }

      return result
    }
  } catch (error) {
    console.error('Could not refresh auth0 accesstoken', error)
  }

  // fallback to old token
  return {
    accessToken,
    accessTokenExpires,
    idToken,
    refreshToken,
  }
}

export const createUser = async (
  bearerToken: string,
  email: string,
  password: string,
  language: string,
): Promise<{ status: number; userId: string }> => {
  const result = await fetch(
    `${process.env.AUTH0_ISSUER_BASE_URL}/api/v2/users`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${bearerToken}`,
      },

      body: JSON.stringify({
        blocked: false,
        connection: 'Username-Password-Authentication',
        email_verified: true,
        email,
        password,
        verify_email: false,
        user_metadata: { locale: language },
      }),
    },
  )

  if (result.status === 200 || result.status === 201) {
    const userData = await result.json()
    return { status: result.status, userId: userData.user_id }
  }

  // If user already exists (409), look them up to get their userId
  if (result.status === 409) {
    try {
      const users = await getAuth0UsersByEmail(bearerToken, email)
      if (users && users.length > 0) {
        return { status: result.status, userId: users[0].user_id }
      }
    } catch (error) {
      console.error(`Failed to lookup existing user ${email}:`, error)
    }
  }

  return { status: result.status, userId: '' }
}
